'use client';
import React, { useState, useEffect } from 'react';

// 横屏提示组件
const HorizontalScreenWarn = ({ close }: { close: () => void }) => {
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes spin-slow {
        from { transform: rotate(0deg); }
        to { transform: rotate(360deg); }
      }
      .animate-spin-slow {
        animation: spin-slow 3s linear infinite;
      }

      @keyframes fade-in-up {
        from {
          opacity: 0;
          transform: translateY(30px) scale(0.95);
        }
        to {
          opacity: 1;
          transform: translateY(0) scale(1);
        }
      }
      .animate-fade-in-up {
        animation: fade-in-up 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94) forwards;
      }

      @keyframes ping-slow {
        0%, 100% {
          opacity: 0.3;
          transform: scale(0.8);
        }
        50% {
          opacity: 0.6;
          transform: scale(1.1);
        }
      }
      .animate-ping-slow {
        animation: ping-slow 3s ease-in-out infinite;
      }

      /* 基础样式 (以 ~375px 宽度为基准) */
      .warning-container {
        padding: 20px;
      }
      .warning-card {
        max-width: 280px;
      }
      .icon-container {
        width: 70px;
        height: 70px;
        margin-bottom: 20px;
      }
      .warning-title {
        font-size: 22px;
        margin-bottom: 15px;
      }
      .warning-message {
        font-size: 15px;
        margin-bottom: 20px;
      }
      .dot-container {
        margin-top: 10px;
      }
      .pulse-dot {
        width: 6px;
        height: 6px;
        margin: 0 4px;
      }

      /* 适配更小的屏幕 (<= 320px) */
      @media (max-width: 320px) {
        .warning-container {
          padding: 15px;
        }
        .warning-card {
          max-width: 250px;
        }
        .icon-container {
          width: 60px;
          height: 60px;
          margin-bottom: 15px;
        }
        .warning-title {
          font-size: 20px;
          margin-bottom: 12px;
        }
        .warning-message {
          font-size: 14px;
          margin-bottom: 20px;
        }
      }

      /* 适配更大的屏幕 (>= 414px) */
      @media (min-width: 414px) {
        .warning-container {
          padding: 25px;
        }
        .warning-card {
          max-width: 320px;
        }
        .icon-container {
          width: 80px;
          height: 80px;
          margin-bottom: 25px;
        }
        .warning-title {
          font-size: 24px;
          margin-bottom: 18px;
        }
        .warning-message {
          font-size: 16px;
          margin-bottom: 20px;
        }
      }
    `;
    document.head.appendChild(style);
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  return (
    <div className="touch-none fixed inset-0 bg-black bg-opacity-80 z-50 flex items-center justify-center warning-container">
      <div className="warning-card w-full text-center animate-fade-in-up text-white">
        {/* 屏幕旋转图标 */}
        <div className="icon-container mx-auto animate-spin-slow relative">
          <div className="absolute inset-0 bg-gradient-to-br from-blue-500/20 to-purple-500/20 rounded-full blur-lg animate-ping-slow"></div>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="relative w-full h-full text-white"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={1.5}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
            />
          </svg>
        </div>
        <h2 className="warning-title font-bold">请切换到竖屏模式</h2>
        <p className="text-gray-300 warning-message">
          为了获得最佳体验，请将您的设备旋转至竖屏模式。
        </p>
        {/* 装饰性元素 */}
        {/* <div className="flex justify-center dot-container">
          {[...Array(3)].map((_, i) => (
            <div
              key={i}
              className="pulse-dot bg-white rounded-full animate-pulse"
              style={{
                animationDelay: `${i * 0.2}s`,
                animationDuration: '1.5s',
              }}
            ></div>
          ))}
        </div> */}
        <button
          className="bg-white text-black rounded-full px-3 py-2"
          onClick={() => {
            close();
          }}
        >
          继续横屏使用
        </button>
      </div>
    </div>
  );
};

export const MobileScreenPermission: React.FC<{
  children: React.JSX.Element;
}> = ({ children }) => {
  const [isLandscape, setIsLandscape] = useState(false);
  const [isUserAllow, setIsUserAllow] = useState(false);
  // 检测屏幕方向
  const checkOrientation = () => {
    // 移动设备检测
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

    if (isMobile) {
      // 移动设备使用窗口宽高比判断
      setIsLandscape(window.innerWidth > window.innerHeight);
    } else {
      // 桌面设备始终允许访问
      setIsLandscape(false);
    }
  };

  useEffect(() => {
    // 如果用户允许，则不显示提示
    if (isUserAllow) return;
    // 初始检测
    checkOrientation();
    // 监听窗口大小变化（设备旋转时触发）
    const debouncedCheck = debounce(checkOrientation, 100);
    window.addEventListener('resize', debouncedCheck);

    // 修正：清理函数不返回任何值
    return () => {
      window.removeEventListener('resize', debouncedCheck);
    };
  }, [isUserAllow]);

  // 防抖函数
  const debounce = (func: () => void, delay: number) => {
    let timeoutId: NodeJS.Timeout;
    return () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(func, delay);
    };
  };

  // 如果是横屏，显示提示；否则显示内容
  return (
    <>
      {isLandscape && (
        <HorizontalScreenWarn
          close={() => {
            setIsUserAllow(true);
            setIsLandscape(false);
          }}
        />
      )}
      {children}
    </>
  );
};
